import { useState, useMemo, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Database,
  Send,
  AlertCircle,
  CheckCircle,
  Table as TableIcon,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Lightbulb,
  Columns,
  Filter,
  Link2,
  Calculator,
  BookOpen,
  Calendar,
  PenLine,
  Loader2,
  History,
  FileBarChart,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { parseUtcDate } from "@/lib/dateFormat";
import { QuickQuestionCharts } from "@/components/portal/charts/QuickQuestionCharts";
import { ClarificationCards } from "@/components/portal/knowledgebase-sql/ClarificationCards";
import { ResultsTable } from "./ResultsTable";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  sqlRagApi,
  DataSource,
  SchemaTable,
  QueryHistoryItem as SQLQueryHistoryItem,
} from "@/services/api/sqlRag";
import { useQueryJobs } from "./useQueryJobs";
import {
  QueryHistorySidebar,
  type QueryHistoryItem,
} from "@/components/portal/shared/query-history";
import { QueryFeedback } from "@/components/portal/shared/feedback";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { PromoteToReportingDialog } from "./PromoteToReportingDialog";
import { openReportWizardWithHandoff, useKbPromotion, type KbPromoteConfirmArgs } from "./useKbPromotion";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/usePermissions";

export const Route = createFileRoute("/portal/knowledgebase-sql/query")({
  component: QueryPage,
});


function QueryPage() {
  const { token, user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  // Promoting an answer creates a Reporting datasource, and org_member never
  // creates a data source (Steve 2026-10-15): admin and above only. CP grants
  // `manage` on reporting to org_owner / org_admin / platform_admin and not to
  // org_member, who holds `create` — the same gate DatasourceList uses.
  const canManageReporting = can("manage", "reporting");

  // Data source selection
  const [selectedDataSourceId, setSelectedDataSourceId] = useState<string>("");
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [schema, setSchema] = useState<SchemaTable[]>([]);

  // Query input
  const [question, setQuestion] = useState("");

  // Results display state
  const [showSQL, setShowSQL] = useState(false);
  const {
    analysis: promoteAnalysis,
    commitError: promoteCommitError,
    promoting,
    error: promoteError,
    start: startPromotion,
    confirm: confirmPromotion,
    cancel: cancelPromotion,
    clearError: clearPromoteError,
  } = useKbPromotion(token);
  // Query history state
  const [queryHistory, setQueryHistory] = useState<SQLQueryHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // KB DB query surface tabs: "ask" = current conversation turn (process → clarification → result),
  // "queries" = the active + finished query list. Composer stays persistent above both.
  const [kbTab, setKbTab] = useState<"ask" | "queries">("ask");
  const isMobile = useIsMobile();

  // Load data sources
  const loadDataSources = useCallback(async () => {
    if (!token) return;
    setLoadingDataSources(true);
    try {
      const response = await sqlRagApi.listDataSources(token, false);
      setDataSources(response.filter((ds: DataSource) => ds.is_validated));
    } catch (error) {
      console.error("Failed to load data sources:", error);
      toast.error("Failed to load data sources");
    } finally {
      setLoadingDataSources(false);
    }
  }, [token]);

  // Load schema
  const loadSchema = useCallback(async () => {
    if (!token || !selectedDataSourceId) return;
    try {
      const response = await sqlRagApi.getSchema(token, selectedDataSourceId);
      setSchema(response);
    } catch (error) {
      console.error("Failed to load schema:", error);
    }
  }, [token, selectedDataSourceId]);

  // Fetch data sources
  useEffect(() => {
    if (token) {
      loadDataSources();
    }
  }, [token, loadDataSources]);

  // Fetch schema when data source changes
  useEffect(() => {
    if (token && selectedDataSourceId) {
      loadSchema();
    }
  }, [token, selectedDataSourceId, loadSchema]);

  // Load query history
  const loadQueryHistory = useCallback(async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const response = await sqlRagApi.getQueryHistory(
        token,
        selectedDataSourceId || undefined,
        100,
      );
      setQueryHistory(response);
    } catch (error) {
      console.error("Failed to load query history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token, selectedDataSourceId]);

  // Load history when data source changes or on mount
  useEffect(() => {
    if (token) {
      loadQueryHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadQueryHistory recreates on selectedDataSourceId change, which is already a dependency
  }, [token, selectedDataSourceId]);

  // Transform SQL history to sidebar format
  const historyItems: QueryHistoryItem[] = useMemo(() => {
    return queryHistory.map((item) => ({
      id: item.id,
      title: item.user_question,
      timestamp: item.created_at ? parseUtcDate(item.created_at) : new Date(),
      status: item.error_message ? "error" : "success",
      feedback: item.user_feedback as "positive" | "negative" | undefined,
      metadata: {
        label: item.generated_sql ? "SQL Generated" : "No SQL",
        secondary:
          item.row_count !== null ? `${item.row_count} rows` : undefined,
      },
    }));
  }, [queryHistory]);

  // Job/polling/conversation/clarification logic (extracted hook).
  const {
    jobs,
    selectedJobId,
    setSelectedJobId,
    selectedJob,
    result,
    ambiguities,
    hasActiveJobs,
    conversationTurns,
    hasActiveConversation,
    conversationBusy,
    followUpQuestion,
    setFollowUpQuestion,
    clarifications,
    otherClarifications,
    setOtherClarifications,
    learnPreferences,
    setLearnPreferences,
    selectedHistoryId,
    handleHistorySelect,
    handleNewQuery,
    handleAskQuestion,
    handleAskFollowUp,
    handleSubmitClarifications,
    handleClarificationChange,
    handleDismissJob,
    phaseLabel,
    setJobs,
  } = useQueryJobs({
    token,
    organisationId: selectedOrganisation?.organisationId,
    userEmail: user?.email,
    selectedDataSourceId,
    setSelectedDataSourceId,
    dataSources,
    question,
    setQuestion,
    queryHistory,
    loadQueryHistory,
    setKbTab,
  });

  // Jobs not yet resolved — same definition the Queries tab list uses to split
  // "Running / Pending" from finished (query.tsx activeJobs below): queued,
  // running, or waiting on the user's clarification answer. Kept as the single
  // source of truth so the tab badge and the list agree on what "still open"
  // means — a job here is exactly a job the list still shows above the fold.
  const unresolvedJobCount = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === "queued" ||
          j.status === "running" ||
          j.status === "needs_clarification",
      ).length,
    [jobs],
  );

  // Extract sample questions from all tables/views
  const sampleQuestions = useMemo(() => {
    const questions: Array<{ question: string; tableName: string }> = [];
    schema.forEach((table: SchemaTable) => {
      // A grant-errored view fails the exact question it would suggest — see
      // plan kbdb-sync-grant-validation D8 (the LLM/MCP paths already exclude
      // it; this is the same rule applied to the FE's own suggestion list).
      if (table.grant_error) return;
      if (table.sample_questions && table.sample_questions.length > 0) {
        table.sample_questions.forEach((q) => {
          questions.push({ question: q, tableName: table.table_name });
        });
      }
    });
    return questions;
  }, [schema]);

  const handleCopySql = async (sql: string) => {
    try {
      await navigator.clipboard.writeText(sql);
      toast.success("SQL copied to clipboard");
    } catch {
      toast.error("Could not copy SQL");
    }
  };

  // Copy the plain-English narration.
  const handleCopyNarration = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Answer copied to clipboard");
    } catch {
      toast.error("Could not copy answer");
    }
  };

  const handlePromoteToReporting = () => {
    if (!result?.log_id) return;
    void startPromotion(result.log_id);
  };

  const handlePromoteConfirm = (args: KbPromoteConfirmArgs) =>
    confirmPromotion(args, (created) => {
      const opened = openReportWizardWithHandoff(created, result?.narration, result?.log_id);
      toast.success(
        `Created datasource "${created.name}"`,
        opened ? undefined : { description: "The report builder could not be opened automatically." },
      );
    });

  /**
   * Re-ask with the columns spelled out (D27's recovery for SELECT *).
   *
   * Pre-fills the box and returns the user to the Ask tab rather than
   * submitting outright: the submit handler reads the question from state, so
   * calling it in the same tick would send the PREVIOUS question.
   */
  const handleAskWithExplicitColumns = (columns: string[]) => {
    const base = result?.question || selectedJob?.question || "";
    setQuestion(`${base}. List these columns explicitly: ${columns.join(", ")}`);
    clearPromoteError();
    setKbTab("ask");
  };

  const historySidebar = (
      <QueryHistorySidebar
        title="Query History"
        icon={History}
        items={historyItems}
        currentId={selectedHistoryId}
        onSelect={(id) => {
          handleHistorySelect(id);
          setMobileSidebarOpen(false);
        }}
        onNew={() => {
          handleNewQuery();
          setMobileSidebarOpen(false);
        }}
        isLoading={loadingHistory}
        searchPlaceholder="Search queries..."
        newButtonLabel="+ New Query"
        filters={
          // Hide the data-source filter while a conversation is active — it
          // would otherwise change selectedDataSourceId and bypass the picker
          // lock. "New conversation" restores it.
          dataSources.length > 1 && !hasActiveConversation
            ? [
                {
                  id: "dataSource",
                  label: "Data Source",
                  options: [
                    { value: "all", label: "All Data Sources" },
                    ...dataSources.map((ds) => ({
                      value: ds.id,
                      // Mark retired sources everywhere they are offered, not
                      // just on the data-sources page. Per D8 a retired source
                      // stays queryable on purpose — so the user must be able
                      // to tell they are querying one, or "why is this data
                      // stale?" has no visible answer.
                      label: ds.retired_at_utc ? `${ds.name} (retired)` : ds.name,
                    })),
                  ],
                  value: selectedDataSourceId || "all",
                  onChange: (value) =>
                    setSelectedDataSourceId(value === "all" ? "" : value),
                },
              ]
            : undefined
        }
      />
    );

    return (
    <TooltipProvider>
      <div className="flex h-full">
        {/* Query History Sidebar — Desktop inline, Mobile Sheet */}
        {isMobile ? (
          <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <SheetContent side="left" className="w-80 p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Query History</SheetTitle>
                <SheetDescription>Your query history</SheetDescription>
              </SheetHeader>
              {historySidebar}
            </SheetContent>
          </Sheet>
        ) : (
          <div className="w-80 flex-shrink-0 border-r">
            {historySidebar}
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-4 space-y-4 px-3 md:py-6 md:space-y-6 md:px-6">
            {/* Header */}
            <div className="flex items-center gap-3">
              {isMobile && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <History className="h-4 w-4" />
                </Button>
              )}
              <div>
                <h1 className="text-2xl md:text-4xl font-bold text-slate-900">
                  Ask Database
                </h1>
                <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
                  Ask questions in plain English and get answers from your
                  connected databases
                </p>
              </div>
            </div>

            {/* Query Input */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Ask a Question
                </CardTitle>
                <CardDescription>
                  Select a database and type your question in natural language
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Data Source Selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Data Source</Label>
                    {hasActiveConversation && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleNewQuery}
                      >
                        <Sparkles className="mr-1 h-3 w-3" />
                        New conversation
                      </Button>
                    )}
                  </div>
                  <Select
                    value={selectedDataSourceId}
                    onValueChange={setSelectedDataSourceId}
                    disabled={loadingDataSources || hasActiveConversation}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a database..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dataSources.map((ds: DataSource) => (
                        <SelectItem key={ds.id} value={ds.id}>
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4" />
                            <span>{ds.name}</span>
                            {ds.retired_at_utc && (
                              <Badge variant="secondary" className="text-xs">
                                Retired
                              </Badge>
                            )}
                            {ds.database_name && (
                              <span className="text-muted-foreground text-xs">
                                ({ds.database_name})
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasActiveConversation && (
                    <p className="text-xs text-muted-foreground">
                      Data source is locked for this conversation. Start a new
                      conversation to change it.
                    </p>
                  )}
                  {dataSources.length === 0 && !loadingDataSources && (
                    <p className="text-sm text-muted-foreground">
                      No databases configured. Contact your administrator to set
                      up a data source.
                    </p>
                  )}
                </div>

                {/* Question Input */}
                <div className="space-y-2">
                  <Label>Your Question</Label>
                  <Textarea
                    placeholder="e.g., What are the top 10 customers by revenue in the last 6 months?"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAskQuestion();
                      }
                    }}
                    rows={3}
                  />
                </div>

                {/* Sample Questions */}
                {sampleQuestions.length > 0 && !question && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Sparkles className="h-4 w-4" />
                      Try a sample question
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {sampleQuestions.slice(0, 6).map((sq, idx) => (
                        <Tooltip key={idx}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setQuestion(sq.question)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-muted hover:bg-muted/80 rounded-full transition-colors text-left max-w-xs truncate"
                            >
                              <span className="truncate">{sq.question}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-sm">
                            <p className="font-medium">{sq.question}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              From: {sq.tableName}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex items-center justify-between">
                  {hasActiveJobs && (
                    <p className="text-xs text-muted-foreground">
                      <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                      {jobs.filter((j) => j.status === "queued" || j.status === "running").length} query(ies) running
                    </p>
                  )}
                  <div className="ml-auto">
                    <Button
                      onClick={handleAskQuestion}
                      disabled={!selectedDataSourceId || !question.trim()}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Ask Question
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ask / Queries tabs — Ask shows the current turn (process → clarification → result);
                Queries holds the active + finished query list. Composer above stays persistent. */}
            <Tabs
              value={kbTab}
              onValueChange={(v) => setKbTab(v as "ask" | "queries")}
            >
              <TabsList>
                <TabsTrigger value="ask">Ask</TabsTrigger>
                <TabsTrigger value="queries">
                  {/* Count only unresolved jobs (queued/running/needs_clarification) —
                      the same set the Queries list itself treats as "still open"
                      (see unresolvedJobCount above and the matching activeJobs
                      filter below). Once nothing is unresolved the badge shows no
                      number: a static total that includes failed jobs is exactly
                      the "still stuck" reading this fix exists to remove, since it
                      never shrinks until "Clear finished" is clicked. */}
                  Queries{unresolvedJobCount > 0 ? ` (${unresolvedJobCount})` : ""}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Conversation thread nav (Ask tab) — the turns of the active thread.
                The selected turn shows in full below; other turns are clickable to
                revisit. Only shown once a thread has more than one turn. */}
            {kbTab === "ask" && conversationTurns.length > 1 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <History className="h-4 w-4" />
                    Conversation · {conversationTurns.length} turns
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {conversationTurns.map((turn, idx) => {
                    const isSelected = turn.jobId === selectedJobId;
                    return (
                      <button
                        key={turn.jobId}
                        type="button"
                        onClick={() => setSelectedJobId(turn.jobId)}
                        className={`flex w-full flex-col gap-1 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          isSelected
                            ? "border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30"
                            : "border-transparent hover:bg-muted"
                        }`}
                      >
                        {/* Original question — always shown in full */}
                        <span className="flex w-full items-start gap-2">
                          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                            {idx + 1}
                          </span>
                          <span className="flex-1 font-medium">{turn.question}</span>
                          {turn.status === "completed" &&
                            turn.result?.row_count !== null &&
                            turn.result?.row_count !== undefined && (
                              <span className="flex-shrink-0 text-xs text-muted-foreground">
                                {turn.result.row_count} rows
                              </span>
                            )}
                          {(turn.status === "queued" ||
                            turn.status === "running") && (
                            <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin text-muted-foreground" />
                          )}
                          {turn.status === "needs_clarification" && (
                            <HelpCircle className="h-3 w-3 flex-shrink-0 text-amber-500" />
                          )}
                        </span>
                        {/* Narrative reply — always shown when available */}
                        {turn.status === "completed" && turn.result?.narration && (
                          <span className="flex items-start gap-1.5 pl-7 text-xs text-muted-foreground">
                            <Sparkles className="mt-0.5 h-3 w-3 flex-shrink-0 text-blue-500" />
                            <span className="flex-1">{turn.result.narration}</span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Running Query Panel (Ask tab) — live process Detect → Generate → Validate → Execute */}
            {kbTab === "ask" &&
              selectedJob &&
              (selectedJob.status === "queued" ||
                selectedJob.status === "running") && (
              <Card className="border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {phaseLabel(selectedJob.phase, selectedJob.status)}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {selectedJob.question}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {selectedJob.dataSourceName && (
                        <span className="flex items-center gap-1">
                          <Database className="h-3.5 w-3.5" />
                          {selectedJob.dataSourceName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Started {selectedJob.startedAt.toLocaleTimeString()}
                      </span>
                    </div>
                    {/* Phase progress */}
                    {(() => {
                      const phases = [
                        "detecting",
                        "consulting",
                        "reviewing",
                        "generating",
                        "validating",
                        "executing",
                      ];
                      const currentIdx = selectedJob.phase
                        ? phases.indexOf(selectedJob.phase)
                        : -1;
                      return (
                        <>
                          <div className="flex gap-2">
                            {phases.map((step) => {
                              const stepIdx = phases.indexOf(step);
                              const isActive = stepIdx === currentIdx;
                              const isDone = stepIdx < currentIdx;
                              return (
                                <div
                                  key={step}
                                  data-testid={`stage-${step}`}
                                  data-state={isDone ? "done" : isActive ? "active" : "todo"}
                                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                                    isDone
                                      ? "bg-blue-500"
                                      : isActive
                                        ? "bg-blue-400 animate-pulse"
                                        : "bg-gray-200 dark:bg-gray-700"
                                  }`}
                                />
                              );
                            })}
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Read</span>
                            <span>Check</span>
                            <span>Review</span>
                            <span>Generate</span>
                            <span>Validate</span>
                            <span>Execute</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Jobs Panel (Queries tab) — active + finished query list */}
            {kbTab === "queries" && jobs.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Loader2 className={`h-4 w-4 ${hasActiveJobs ? "animate-spin text-blue-500" : "text-muted-foreground"}`} />
                      Queries
                      {hasActiveJobs && (
                        <Badge variant="secondary" className="text-xs">
                          {jobs.filter((j) => j.status === "queued" || j.status === "running").length} running
                        </Badge>
                      )}
                    </CardTitle>
                    {jobs.some((j) => j.status === "completed" || j.status === "failed") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => {
                          setJobs((prev) =>
                            prev.filter(
                              (j) => j.status === "queued" || j.status === "running" || j.status === "needs_clarification" || j.jobId === selectedJobId,
                            ),
                          );
                        }}
                      >
                        Clear finished
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Running / Pending jobs */}
                  {(() => {
                    const activeJobs = jobs.filter(
                      (j) => j.status === "queued" || j.status === "running" || j.status === "needs_clarification",
                    );
                    const finishedJobs = jobs.filter(
                      (j) => j.status === "completed" || j.status === "failed",
                    );
                    return (
                      <>
                        {activeJobs.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Running
                            </h4>
                            {activeJobs.map((job) => (
                              <div
                                key={job.jobId}
                                role="button"
                                tabIndex={0}
                                className={`flex items-center gap-3 p-3 rounded-lg border-l-4 cursor-pointer transition-all animate-pulse ${
                                  selectedJobId === job.jobId
                                    ? "border-l-blue-500 bg-blue-100/60 dark:bg-blue-950/40 ring-1 ring-blue-300/40"
                                    : "border-l-blue-400 bg-blue-50/50 dark:bg-blue-950/20 hover:bg-blue-100/40 dark:hover:bg-blue-950/30"
                                }`}
                                onClick={() => {
                                  setSelectedJobId(job.jobId);
                                  setKbTab("ask");
                                }}
                                onKeyDown={(e) =>
                                  e.key === "Enter" && setSelectedJobId(job.jobId)
                                }
                              >
                                {job.status === "needs_clarification" ? (
                                  <HelpCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                ) : (
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-500 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {job.question}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-blue-600/70 dark:text-blue-400/70">
                                    <span>{phaseLabel(job.phase, job.status)}</span>
                                    {job.dataSourceName && (
                                      <>
                                        <span>·</span>
                                        <span>{job.dataSourceName}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {finishedJobs.length > 0 && (
                          <div className="space-y-2">
                            {activeJobs.length > 0 && (
                              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">
                                Completed
                              </h4>
                            )}
                            {finishedJobs.map((job) => (
                              <div
                                key={job.jobId}
                                role="button"
                                tabIndex={0}
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  selectedJobId === job.jobId
                                    ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                                    : "border-transparent bg-muted/50 hover:bg-muted"
                                }`}
                                onClick={() => {
                                  setSelectedJobId(job.jobId);
                                  setKbTab("ask");
                                }}
                                onKeyDown={(e) =>
                                  e.key === "Enter" && setSelectedJobId(job.jobId)
                                }
                              >
                                {job.status === "completed" && job.result?.success ? (
                                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {job.question}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>{phaseLabel(job.phase, job.status)}</span>
                                    {job.result?.row_count != null && (
                                      <>
                                        <span>·</span>
                                        <span>{job.result.row_count} rows</span>
                                      </>
                                    )}
                                    {job.result?.execution_time_ms != null && (
                                      <>
                                        <span>·</span>
                                        <span>{job.result.execution_time_ms}ms</span>
                                      </>
                                    )}
                                    {job.dataSourceName && (
                                      <>
                                        <span>·</span>
                                        <span>{job.dataSourceName}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDismissJob(job.jobId);
                                  }}
                                >
                                  ×
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Clarification Questions (Ask tab) */}
            {kbTab === "ask" &&
              ambiguities.length > 0 &&
              selectedJob?.status === "needs_clarification" && (
              <ClarificationCards
                ambiguities={ambiguities}
                clarifications={clarifications}
                otherClarifications={otherClarifications}
                onClarificationChange={handleClarificationChange}
                onOtherClarificationChange={(id, text) =>
                  setOtherClarifications((prev) => ({ ...prev, [id]: text }))
                }
                learnPreferences={learnPreferences}
                onLearnPreferencesChange={setLearnPreferences}
                onSubmit={handleSubmitClarifications}
              />
            )}

            {/* Results (Ask tab) */}
            {kbTab === "ask" && result && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {result.success ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          Results
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-5 w-5 text-red-500" />
                          Query Failed
                        </>
                      )}
                    </CardTitle>
                  </div>
                  {result.success && (
                    <CardDescription>
                      {result.row_count} rows returned
                      {result.execution_time_ms &&
                        ` in ${result.execution_time_ms}ms`}
                      {result.truncated && " (results truncated)"}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Clarifications applied to this turn (chips) */}
                  {selectedJob?.submittedClarifications &&
                    Object.keys(selectedJob.submittedClarifications).length >
                      0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">
                          You clarified:
                        </span>
                        {Object.entries(
                          selectedJob.submittedClarifications,
                        ).map(([id, value]) => (
                          <Badge key={id} variant="secondary" className="text-xs">
                            {value}
                          </Badge>
                        ))}
                      </div>
                    )}

                  {/* Explanation */}
                  <div className="p-3 bg-muted rounded-md">
                    <p className="text-sm">{result.explanation}</p>
                  </div>

                  {/* Narration — plain English answer */}
                  {result.narration && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
                      <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                              Plain English Answer
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 gap-1 px-1.5 text-xs text-blue-700 dark:text-blue-300 -mt-1"
                              onClick={() =>
                                handleCopyNarration(result.narration!)
                              }
                            >
                              <Copy className="h-3 w-3" />
                              Copy
                            </Button>
                          </div>
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            {result.narration}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {result.warnings && result.warnings.length > 0 && (
                    <div className="space-y-2">
                      {result.warnings.map((warning, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 p-2 bg-yellow-50 dark:bg-yellow-950/20 rounded text-sm"
                        >
                          <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                          <span>{warning}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* SQL (Collapsible) */}
                  {result.sql && (
                    <Collapsible open={showSQL} onOpenChange={setShowSQL}>
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        {showSQL ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        View Generated SQL
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <pre className="mt-2 p-3 bg-slate-900 text-slate-50 rounded-md overflow-x-auto text-sm">
                          <code>{result.sql}</code>
                        </pre>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Why this SQL? (Reasoning Trace) */}
                  {result.reasoning && (
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <Lightbulb className="h-4 w-4" />
                        Why this SQL?
                        <ChevronRight className="h-4 w-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-3 space-y-4 p-4 bg-muted/50 rounded-md">
                          {/* Tables Selected */}
                          {result.reasoning.tables_selected.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <TableIcon className="h-4 w-4 text-blue-500" />
                                Tables Selected
                              </h4>
                              <ul className="space-y-1">
                                {result.reasoning.tables_selected.map(
                                  (t, i) => (
                                    <li key={i} className="text-sm pl-6">
                                      <span className="font-mono text-primary">
                                        {t.table}
                                      </span>
                                      <span className="text-muted-foreground">
                                        {" "}
                                        — {t.reason}
                                      </span>
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Columns Selected */}
                          {result.reasoning.columns_selected.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <Columns className="h-4 w-4 text-green-500" />
                                Columns Used
                              </h4>
                              <ul className="space-y-1">
                                {result.reasoning.columns_selected.map(
                                  (c, i) => (
                                    <li key={i} className="text-sm pl-6">
                                      <span className="font-mono text-primary">
                                        {c.column}
                                      </span>
                                      <span className="text-muted-foreground">
                                        {" "}
                                        — {c.purpose}
                                      </span>
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Joins Applied */}
                          {result.reasoning.joins_applied.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <Link2 className="h-4 w-4 text-purple-500" />
                                Joins Applied
                              </h4>
                              <ul className="space-y-1">
                                {result.reasoning.joins_applied.map((j, i) => (
                                  <li
                                    key={i}
                                    className="text-sm pl-6 text-muted-foreground"
                                  >
                                    {j}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Filters Applied */}
                          {result.reasoning.filters_applied.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <Filter className="h-4 w-4 text-orange-500" />
                                Filters Applied
                              </h4>
                              <ul className="space-y-1">
                                {result.reasoning.filters_applied.map(
                                  (f, i) => (
                                    <li
                                      key={i}
                                      className="text-sm pl-6 text-muted-foreground"
                                    >
                                      {f}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Aggregations */}
                          {result.reasoning.aggregations.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <Calculator className="h-4 w-4 text-cyan-500" />
                                Aggregations
                              </h4>
                              <ul className="space-y-1">
                                {result.reasoning.aggregations.map((a, i) => (
                                  <li
                                    key={i}
                                    className="text-sm pl-6 text-muted-foreground"
                                  >
                                    {a}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Glossary Terms Used */}
                          {result.reasoning.glossary_terms_used.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <BookOpen className="h-4 w-4 text-indigo-500" />
                                Business Glossary Applied
                              </h4>
                              <div className="flex flex-wrap gap-2 pl-6">
                                {result.reasoning.glossary_terms_used.map(
                                  (t, i) => (
                                    <Badge key={i} variant="secondary">
                                      {t}
                                    </Badge>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {/* Preferences Applied */}
                          {result.reasoning.preferences_applied.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                Learned Preferences Applied
                              </h4>
                              <div className="flex flex-wrap gap-2 pl-6">
                                {result.reasoning.preferences_applied.map(
                                  (p, i) => (
                                    <Badge key={i} variant="outline">
                                      {p}
                                    </Badge>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {/* Reasoning Steps (if any) */}
                          {result.reasoning.steps.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">
                                Step-by-Step Reasoning
                              </h4>
                              <ol className="space-y-2 pl-6 list-decimal list-inside">
                                {result.reasoning.steps.map((step, i) => (
                                  <li
                                    key={i}
                                    className="text-sm text-muted-foreground"
                                  >
                                    <span className="font-medium text-foreground">
                                      {step.step}:
                                    </span>{" "}
                                    {step.description}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Analyst-proposed charts (plan kbdb-ask-database-charts D10) — same
                      position as Quick Question: charts, then the table. */}
                  {result.success && (
                    <QuickQuestionCharts
                      charts={result.charts ?? []}
                      columns={result.columns}
                      rows={result.rows}
                      truncated={result.truncated}
                      question={result.question}
                    />
                  )}

                  {/* Data Table */}
                  {result.success &&
                    result.columns &&
                    result.rows &&
                    result.rows.length > 0 && (
                      <ResultsTable
                        key={selectedJobId}
                        columns={result.columns}
                        rows={result.rows as Record<string, unknown>[]}
                        question={result.question}
                      />
                    )}

                  {/* No Results */}
                  {result.success &&
                    (!result.rows || result.rows.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <TableIcon className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold">No Results</h3>
                        <p className="text-muted-foreground">
                          The query executed successfully but returned no data.
                        </p>
                      </div>
                    )}

                  {/* Feedback + actions */}
                  <div className="border-t pt-4 flex items-center justify-between">
                    {result.log_id && (
                      <QueryFeedback
                        queryType="sql_rag"
                        queryId={result.log_id}
                        onFeedbackSubmit={() => loadQueryHistory()}
                      />
                    )}
                    <div className="flex items-center gap-2">
                      {result.success && result.sql && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopySql(result.sql!)}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy SQL
                        </Button>
                      )}
                      {/* Hidden for a turn that could never be promoted (D27):
                          no SQL, or no query-log id, means there is nothing
                          durable to build a datasource from. Every other
                          refusal is a reason the user should HEAR, so it
                          surfaces inline below rather than as a hidden button. */}
                      {result.success &&
                        result.sql &&
                        result.log_id &&
                        canManageReporting && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePromoteToReporting}
                            disabled={promoting}
                          >
                            {promoting ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <FileBarChart className="h-4 w-4 mr-2" />
                            )}
                            Promote to Reporting
                          </Button>
                        )}
                    </div>
                    {promoteError && (
                      <div
                        role="alert"
                        className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                      >
                        <p>{promoteError.message}</p>
                        {promoteError.code === "select_star_not_allowed" &&
                          promoteError.columns &&
                          promoteError.columns.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              onClick={() =>
                                handleAskWithExplicitColumns(
                                  promoteError.columns!,
                                )
                              }
                            >
                              Ask again with these columns
                            </Button>
                          )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Follow-up composer (Ask tab) — persistent at the bottom of a live
                conversation. Builds on the thread: same data source, grounded on
                the prior turns. */}
            {kbTab === "ask" && hasActiveConversation && (
              <Card className="border-dashed">
                <CardContent className="space-y-2 pt-4">
                  <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <PenLine className="h-4 w-4" />
                    Build on this answer
                  </Label>
                  <Textarea
                    placeholder="e.g., add a column for donor count, or only show rows over 10,000"
                    value={followUpQuestion}
                    onChange={(e) => setFollowUpQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!conversationBusy) handleAskFollowUp();
                      }
                    }}
                    rows={2}
                  />
                  <div className="flex items-center justify-end">
                    <Button
                      onClick={handleAskFollowUp}
                      disabled={!followUpQuestion.trim() || conversationBusy}
                    >
                      {conversationBusy ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      Ask follow-up
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <PromoteToReportingDialog
        open={promoteAnalysis !== null}
        analysis={promoteAnalysis}
        submitting={promoting}
        commitError={promoteCommitError}
        onCancel={cancelPromotion}
        onConfirm={handlePromoteConfirm}
      />
    </TooltipProvider>
  );
}
